<template>
  <div class="create-container">
    <div class="create-card">
      <h2 class="create-title">Create Your Account</h2>
      <p class="create-subtitle">
        Complete your registration to access your RTH Group dashboard
      </p>

      <form
        @submit.prevent="handleCreateAccount"
        class="create-form"
        v-if="validToken"
      >
        <label>Email Address</label>
        <input type="email" :value="email" disabled />

        <label>Full Name</label>
        <input
          type="text"
          v-model="displayName"
          placeholder="Your full name"
          required
        />

        <label>Create Password</label>
        <input type="password" v-model="password" required />

        <label>Confirm Password</label>
        <input type="password" v-model="confirmPassword" required />

        <button class="create-button" :disabled="loading">
          {{ loading ? "Creating Account..." : "Create Account" }}
        </button>

        <p v-if="error" class="error-msg">{{ error }}</p>
        <p v-if="success" class="success-msg">{{ success }}</p>
      </form>

      <p v-else class="error-msg">
        Invalid or expired invite link. Please contact your administrator.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { auth } from "@/firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

const route = useRoute();
const router = useRouter();
const functions = getFunctions();

const token = route.query.token;
const email = ref("");
const password = ref("");
const confirmPassword = ref("");
const displayName = ref("");
const loading = ref(false);
const error = ref("");
const success = ref("");
const validToken = ref(false);

let userRole = null; // ← Captured from the accepted invite

onMounted(async () => {
  if (!token) return;

  // 1) Look up the invite server-side (account records aren't publicly readable).
  //    Invalid, used, blocked or removed invites all show the same "invalid link" view.
  try {
    const { data } = await httpsCallable(functions, "lookupInvite")({ token });
    email.value = data.email;
    validToken.value = true;
  } catch (err) {
    validToken.value = false;
  }
});

const handleCreateAccount = async () => {
  if (password.value !== confirmPassword.value) {
    error.value = "Passwords do not match.";
    return;
  }

  loading.value = true;
  error.value = "";
  try {
    // 2) Create the Auth user
    const userCred = await createUserWithEmailAndPassword(
      auth,
      email.value,
      password.value
    );

    // 3) Link the login to the invited account record (sets status, UID, clears token, sets role claim)
    const { data } = await httpsCallable(functions, "acceptInvite")({
      token,
      displayName: displayName.value,
    });
    userRole = data.role;
    // Pick up the role claim set by acceptInvite
    await userCred.user.getIdToken(true);

    success.value = "Account created successfully! Redirecting...";

    // 4) After a brief delay, redirect based on role
    setTimeout(() => {
      if (userRole === "admin") {
        router.push("/admin");
      } else if (userRole === "employee") {
        router.push("/my-paystubs");
      } else if (userRole === "user" || userRole === "applicant") {
        router.push("/my-applications");
      } else {
        router.push("/");
      }
    }, 1500);
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.create-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f1f5f9;
}

.create-card {
  background: #ffffff;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.create-title {
  font-size: 1.75rem;
  text-align: center;
  margin-bottom: 0.25rem;
  font-weight: 700;
}

.create-subtitle {
  font-size: 0.9rem;
  color: #555;
  text-align: center;
  margin-bottom: 1.5rem;
}

.create-form {
  display: flex;
  flex-direction: column;
}

.create-form label {
  margin-bottom: 0.25rem;
  font-weight: 500;
  font-size: 0.9rem;
}

.create-form input {
  padding: 0.65rem 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.create-button {
  background-color: #1d4ed8;
  color: #fff;
  font-weight: bold;
  border: none;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
}

.create-button:hover {
  background-color: #2563eb;
}

.create-button:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.error-msg {
  margin-top: 1rem;
  color: red;
  text-align: center;
  font-size: 0.9rem;
}

.success-msg {
  margin-top: 1rem;
  color: green;
  text-align: center;
  font-size: 0.9rem;
}
</style>
